<template>
  <div>
    <v-card elevation="4" class="data-table">
      <!-- <v-data-table
        calculate-widths
        :headers="headers"
        :search="search"
        :items="items"
        :items-per-page="itemsPerPage"
        :page.sync="page"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        hide-default-footer
        class="elevation-0 custom-data-table"
        :loading="loading"
      > -->
      <v-data-table
        calculate-widths
        :headers="headers"
        :items="items"
        :items-per-page.sync="itemsPerPage"
        :page.sync="page"
        :search="searchTerm"
        :sort-by="sortBy.value"
        :sort-desc="sortDesc"
        hide-default-footer
        hide-default-header
        mobile-breakpoint="200"
        class="elevation-0 custom-data-table"
        :server-items-length="itemsTotal"
        :loading="loading"
      >
        <template v-slot:item.id="{ item }">
          <b>{{ item.number || item.id }}</b>
        </template>

        <template v-slot:item.duedate="{ item }">
          <span
            v-if="
              item.status != 'Unpaid' ||
              !moment(item.duedate).isBefore(moment())
            "
          >
            {{ item.duedate }}
          </span>
          <div v-else class="d-flex align-center text--error-base">
            <span class="mr-2">{{ item.duedate }}</span>
            <v-tooltip open-delay="150" bottom transition="custom-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="cursor-pointer"
                  color="error"
                  v-on="on"
                  v-bind="attrs"
                  size="16"
                  >$alertcircle</v-icon
                >
              </template>
              <div>
                <p class="p-3 mb-0">
                  {{ $t("tooltip.billing.invoice.duedate.exceeded") }}
                </p>
              </div>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.total="{ item }">
          {{ item.total_formatted }}
        </template>

        <template v-slot:item.status="{ item }">
          <StatusLabel :status="item.status.toLowerCase()" />
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end align-center">
            <v-btn
              v-if="item.status == 'Unpaid'"
              :loading="payInvoiceUrlLoading"
              @click="getPayInvoiceUrl(item.id)"
              color=""
              class="px-2"
              outlined
              small
              elevation="0"
            >
              <span class="font-weight-bold text--gray-darken1">{{
                $t("button.billing.pay_now")
              }}</span>
            </v-btn>
            <v-tooltip open-delay="150" bottom transition="custom-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="downloadInvoice(item.id)"
                  v-on="on"
                  v-bind="attrs"
                  color=""
                  class="btn-icon px-2 ml-2"
                  outlined
                  square
                  small
                  elevation="0"
                >
                  <v-icon :size="16">$download</v-icon>
                </v-btn>
              </template>
              <div>
                <p class="p-3 mb-0">
                  {{ $t("Download") }}
                </p>
              </div>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:body v-if="loading">
          <template v-if="$vuetify.breakpoint.lgAndDown">
            <invoice-table-item-skeleton-mobile v-for="item in 5" :key="item" />
          </template>
          <template v-else>
            <invoice-table-skeleton-sort />
            <invoice-table-skeleton-item />
          </template>
        </template>

        <template v-slot:no-data>
          <template v-if="!loading && !searchTerm">
            <table-full-screen-message :title="$t('heading.billing.invoice.noData.title')">
            </table-full-screen-message>
          </template>
          <data-iterator-no-results-container
            v-if="searchTerm"
            @clearAllFilters="$emit('update:searchTerm', '')"
            :searchTerm="searchTerm"
          />
        </template>

        <template v-if="!loading" v-slot:header="{ isMobile, props, on }">
          <table-custom-sort
            :isMobile="isMobile"
            :props="props"
            :v-on="on"
            v-on="$listeners"
          />
        </template>

        <template v-slot:footer>
          <div>
            <data-iterator-footer
              :page="page"
              :numberOfPages="numberOfPages"
              :possibleItemsPerPage="possibleItemsPerPage"
              :listStyle="listStyle"
              :itemsPerPage="itemsPerPage"
              v-on="$listeners"
              :perPageLabel="$t('table.footer.invoice')"
            >
            </data-iterator-footer>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import DataIteratorMixin from "../../../mixins/DataIteratorMixin";
import CustomTableMixin from "../../../mixins/CustomTableMixin";

import DataIteratorFooter from "../DataIteratorFooter.vue";
import TableCustomSort from "../TableCustomSort.vue";

import InvoiceTableSkeletonItem from "./InvoiceTableSkeletonItem.vue";
import InvoiceTableSkeletonSort from "./InvoiceTableSkeletonSort.vue";
import InvoiceTableItemSkeletonMobile from "./InvoiceTableItemSkeletonMobile.vue";
import TableFullScreenMessage from "../tables/TableFullScreenMessage.vue";
import DataIteratorNoResultsContainer from "../DataIteratorNoResultsContainer.vue";
import StatusLabel from "../../labels/StatusLabel.vue";
import moment from "moment";
import Api from "@/apis/Api.js";

export default {
  props: {
    invoices: Array,
    searchTerm: String,
    itemsTotal: Number,
    loading: Boolean,
  },
  components: {
    DataIteratorFooter,
    TableCustomSort,
    InvoiceTableSkeletonItem,
    InvoiceTableSkeletonSort,
    InvoiceTableItemSkeletonMobile,
    TableFullScreenMessage,
    DataIteratorNoResultsContainer,
    StatusLabel,
  },
  mixins: [DataIteratorMixin, CustomTableMixin],

  data() {
    return {
      itemsLoading: false,
      headers: [
        {
          text: "number",
          value: "id",
          sortable: true,
          searchable: true,
        },
        {
          text: "invoice_date",
          value: "date",
          sortable: true,
          searchable: true,
        },
        {
          text: "due_date",
          value: "duedate",
          sortable: true,
          searchable: true,
        },
        {
          text: "total",
          value: "total",
          sortable: true,
          searchable: true,
        },
        {
          text: "status",
          value: "status",
          sortable: true,
          searchable: true,
        },
        {
          text: " ",
          value: "actions",
          searchable: true,
        },
      ],
      moment,
      payInvoiceUrlLoading: false,
    };
  },
  computed: {
    pageCount() {
      return Math.ceil(this.items.length / this.itemsPerPage);
    },
  },
  methods: {
    getPayInvoiceUrl(id) {
      this.payInvoiceUrlLoading = true;
      Api.post(`/billing/invoices/${id}/payment-url`)
        .then((response) => {
          if (!this.isValidUrl(response.data.data)) {
            throw new Error();
          }
          window.open(response.data.data);
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.payInvoiceUrlLoading = false;
        });
    },
    downloadInvoice(id) {
      let apiUrl = this.$store.state.env.apiUrl;
      window.open(
        `${apiUrl}/billing/invoices/${id}/download`,
        "_blank"
      );
    },
    isValidUrl(string) {
      let url;
      try {
        url = new URL(string);
      } catch (_) {
        return false;
      }
      return url.protocol === "http:" || url.protocol === "https:";
    },
  },
};
</script>